import { v4 as uuid } from 'uuid';
import { Button, FieldList, Link, Radio, tokens, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useTranslation, useTranslationWithPrefix } from '@hotjar/translations';
import type { SurveyTargeting } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import { PagesTargetingModal } from './PagesTargetingModal';
import { PageTargetingRule } from './PageTargetingRule';
import type {
  PageTargetEntry,
  PageTargetingMode,
  TargetEntry,
} from '../../../../common/Wizard/common/targeting';
import {
  getTargetedPagesAndTriggers,
  isTargetingAllPages,
} from '../../../../common/Wizard/common/targeting';
import { useEditingCallbacks } from '../useEditingCallbacks';
import { validateRuleAndNormalize } from './validators';

const StyledRadio = styled(Radio)`
  margin-bottom: 0 !important;
`;

const StyledButton = styled(Button)`
  width: auto;
`;

const StyledFieldList = styled(FieldList)`
  width: auto;
`;

const FieldListWithSeparator = styled(StyledFieldList)`
  & > div {
    gap: 10px;
  }
`;

const TargetingSeparator = styled.div`
  ${tokens.text('caption')}
  color: rgba(0,0,0,0.6);
`;

const SpecificPages = styled.div`
  margin-left: ${tokens.spacer.x2};
  width: auto;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
  align-items: start;
`;

const RuleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x2};
  align-items: start;
`;

const targetingModeInfo: { mode: PageTargetingMode; key: string }[] = [
  { mode: 'all', key: 'options.all' },
  { mode: 'specific', key: 'options.specific.title' },
];

const ALL_PAGES: PageTargetEntry = {
  id: uuid(),
  error: undefined,
  target: { component: 'url', pattern: '/', match_operation: 'contains', negate: false },
};

const DEFAULT_SPECIFIC_ENTRIES: PageTargetEntry[] = [
  {
    id: uuid(),
    error: undefined,
    target: {
      component: 'url',
      match_operation: 'simple',
      pattern: '',
      negate: false,
    },
  },
];

export interface PagesTargetingProps {
  onTargetingChange: (targeting: TargetEntry[]) => void;
  targeting: TargetEntry[];
  onUpsell: (location: string) => void;
  autoValidate: boolean;
  entityType: 'survey' | 'widget' | 'invitation';
  hasErrors: boolean;
}

export function PagesTargeting({
  onTargetingChange,
  targeting,
  onUpsell,
  autoValidate,
  entityType,
  hasErrors,
}: PagesTargetingProps): JSX.Element | null {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.targeting.page');
  const { t: commonT } = useTranslation('common');
  const [isModalOpen, setModalState] = useState(false);
  const [isAutoValidating, setIsAutoValidating] = useState(autoValidate);
  const { onAddRule, onEditRule, onRemoveRule } = useEditingCallbacks(targeting, onTargetingChange);
  const entries = useMemo(() => getTargetedPagesAndTriggers(targeting), [targeting]);
  const validatedAllRef = useRef(false);

  const mode: PageTargetingMode | undefined = useMemo(() => {
    if (entries.length === 0) return undefined;
    return isTargetingAllPages(entries) ? 'all' : 'specific';
  }, [entries]);

  const addRule = useCallback(
    (negative: boolean) => {
      const newRule = {
        negate: negative,
        // eslint-disable-next-line camelcase
        match_operation: 'simple',
        component: 'url',
        pattern: '',
      } as SurveyTargeting;
      onAddRule(newRule, hasErrors ? 'This field cannot be left empty.' : undefined);
    },
    [hasErrors, onAddRule],
  );

  const positiveRules = useMemo(
    () =>
      entries.filter((entry) => entry.target.component !== 'url' || entry.target.negate === false),
    [entries],
  );

  const negativeRules = useMemo(
    () =>
      entries.filter((entry) => entry.target.component === 'url' && entry.target.negate === true),
    [entries],
  );

  // enable auto validation if the existing rules contain errors
  // so that the errors are shown when switching back from 'All pages'
  useLayoutEffect(() => {
    setIsAutoValidating(entries.some((entry) => entry.error) || autoValidate);
  }, [entries, autoValidate]);

  useEffect(() => {
    validatedAllRef.current = hasErrors === false;
  }, [hasErrors]);

  useEffect(() => {
    if (hasErrors && validatedAllRef.current === false) {
      validatedAllRef.current = true;
      onTargetingChange(
        targeting.map((rule) =>
          rule.target.component === 'url' || rule.target.component === 'trigger'
            ? validateRuleAndNormalize(rule as PageTargetEntry)
            : { ...rule },
        ),
      );
    }
  }, [hasErrors, targeting, onTargetingChange]);

  const specificTargetingRef = useRef<PageTargetEntry[]>(
    isTargetingAllPages(entries) ? [] : entries,
  );

  const onPageTargetingModeChange = useCallback(
    (newMode: PageTargetingMode) => {
      if (targeting === undefined) return;
      const otherRules = targeting.filter(
        (entry) => entry.target.component !== 'url' && entry.target.component !== 'trigger',
      );
      if (newMode === 'all') {
        specificTargetingRef.current = entries;
        onTargetingChange([...otherRules, ALL_PAGES]);
        return;
      }
      const specificTargeting =
        specificTargetingRef.current.length === 0
          ? DEFAULT_SPECIFIC_ENTRIES
          : specificTargetingRef.current;
      onTargetingChange([...otherRules, ...specificTargeting]);
    },
    [targeting, entries, onTargetingChange],
  );

  return (
    <>
      <StyledFieldList title={t('title')} helperText={t('description', { entityType })} size="m">
        {targetingModeInfo.map((info) => (
          <StyledRadio
            key={info.mode}
            isChecked={mode === info.mode}
            id={`page_targeting_${info.mode}`}
            name="page_targeting_mode"
            onChange={() => onPageTargetingModeChange(info.mode)}
            value={info.mode}
            labelContent={t(info.key)}
            isInvalid={isAutoValidating && mode === undefined}
          />
        ))}
        {isAutoValidating && mode === undefined && (
          <Text key="validation_message" textColor="text_error">
            {t('validation')}
          </Text>
        )}
      </StyledFieldList>
      {mode === 'specific' && (
        <SpecificPages>
          <RuleList>
            <FieldListWithSeparator
              title={t('options.specific.include.title', { entityType })}
              helperText={
                // eslint-disable-next-line react/jsx-wrap-multilines
                <>
                  {`${t('options.specific.include.description')} `}
                  <Link as="button" kind="inline" onClick={() => setModalState(true)}>
                    {t('options.specific.include.link')}
                  </Link>
                </>
              }
            >
              {positiveRules.map((entry, index) => (
                <Fragment key={entry.id}>
                  {index !== 0 && (
                    <TargetingSeparator>{commonT('or').toLocaleUpperCase()}</TargetingSeparator>
                  )}
                  <PageTargetingRule
                    onChange={onEditRule}
                    onRemove={() => onRemoveRule(entry)}
                    canRemove={positiveRules.length > 1}
                    includeEvents
                    rule={entry}
                    onUpsell={onUpsell}
                  />
                </Fragment>
              ))}
            </FieldListWithSeparator>
            <StyledButton
              title={t('options.specific.include.add')}
              icon="plus"
              kind="secondary"
              onClick={() => addRule(false)}
            >
              {t('options.specific.include.add')}
            </StyledButton>
          </RuleList>
          <RuleList>
            <FieldListWithSeparator
              title={t('options.specific.exclude.title', { entityType })}
              helperText={t('options.specific.exclude.description', { entityType })}
            >
              {negativeRules.map((rule, index) => (
                <Fragment key={rule.id}>
                  {index !== 0 && (
                    <TargetingSeparator>{commonT('or').toLocaleUpperCase()}</TargetingSeparator>
                  )}
                  <PageTargetingRule
                    onChange={onEditRule}
                    onRemove={() => onRemoveRule(rule)}
                    canRemove
                    rule={rule}
                    onUpsell={onUpsell}
                  />
                </Fragment>
              ))}
            </FieldListWithSeparator>
            <StyledButton
              title={t('options.specific.exclude.add')}
              icon="plus"
              kind="secondary"
              onClick={() => addRule(true)}
            >
              {t('options.specific.exclude.add')}
            </StyledButton>
          </RuleList>
        </SpecificPages>
      )}
      <PagesTargetingModal isModalOpen={isModalOpen} onModalClose={() => setModalState(false)} />
    </>
  );
}
